// src/path-segment.ts
var UNSAFE_PATH_SEGMENTS = /* @__PURE__ */ new Set([
  "__proto__",
  "constructor",
  "prototype"
]);
function isUnsafeSegment(part) {
  return UNSAFE_PATH_SEGMENTS.has(part);
}
var CANONICAL_INDEX = /^(0|[1-9]\d*)$/;
function ownPathEntry(root, parts) {
  let cur = root;
  for (const seg of parts) {
    if (Array.isArray(cur)) {
      if (!CANONICAL_INDEX.test(seg) || Number(seg) >= cur.length || !(Number(seg) in cur))
        return { found: false, value: void 0 };
    } else if (typeof cur !== "object" || cur === null || !Object.hasOwn(cur, seg))
      return { found: false, value: void 0 };
    cur = cur[seg];
  }
  return { found: true, value: cur };
}

// src/json-path-set.ts
var CANONICAL_INDEX2 = /^(0|[1-9]\d*)$/;
function containerFor(value, seg, last) {
  if (Array.isArray(value)) {
    if (!last && CANONICAL_INDEX2.test(seg) && Number(seg) < value.length) return value;
    const obj = {};
    for (let i = 0; i < value.length; i++) if (i in value) obj[String(i)] = value[i];
    return obj;
  }
  if (typeof value === "object" && value !== null) return value;
  return {};
}
function holderOf(obj, parts) {
  for (const seg of parts) if (isUnsafeSegment(seg)) throw new Error(`unsafe path segment: ${seg}`);
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const holder = cur;
    const next = containerFor(holder[parts[i]], parts[i + 1], i + 1 === parts.length - 1);
    holder[parts[i]] = next;
    cur = next;
  }
  return cur;
}
function setTreePath(obj, path, value) {
  const parts = path.split(".");
  holderOf(obj, parts)[parts[parts.length - 1]] = value;
}
function deleteTreePath(obj, path) {
  const parts = path.split(".");
  delete holderOf(obj, parts)[parts[parts.length - 1]];
}
var ROOT = "v";
var rooted = (path) => path === "" ? ROOT : `${ROOT}.${path}`;
function readCell(stored) {
  return stored === null || stored === "" ? {} : { v: JSON.parse(stored) };
}
function writeCell(cell) {
  return Object.hasOwn(cell, ROOT) ? JSON.stringify(cell.v) : null;
}
function cellPut(cell, path, value) {
  setTreePath(cell, rooted(path), value);
}
function cellDelete(cell, path) {
  deleteTreePath(cell, rooted(path));
}
function cellRead(cell, path) {
  return ownPathEntry(cell, rooted(path).split("."));
}
function isAncestorOrSelf(maybe, path) {
  return maybe === "" || maybe === path || path.startsWith(`${maybe}.`);
}
function cellFloor(clocks, seed, path) {
  let floor = seed;
  if (clocks) {
    for (const [p, c] of clocks) if (c > floor && isAncestorOrSelf(p, path)) floor = c;
  }
  return floor;
}
var pathDepth = (path) => path === "" ? 0 : path.split(".").length;
function putWinningWrite(cell, clocks, write) {
  const kept = [];
  if (clocks) {
    for (const [p, c] of clocks)
      if (c > write.clock && p !== write.path && isAncestorOrSelf(write.path, p))
        kept.push({ path: p, ...cellRead(cell, p), clock: c });
  }
  kept.sort(
    (a, b) => (a.clock < b.clock ? -1 : a.clock > b.clock ? 1 : 0) || pathDepth(a.path) - pathDepth(b.path)
  );
  if (write.found) cellPut(cell, write.path, structuredClone(write.value));
  else cellDelete(cell, write.path);
  for (const k of kept) {
    if (k.found) cellPut(cell, k.path, k.value);
    else cellDelete(cell, k.path);
  }
}
function applyCellWrite(cell, clocks, seed, write) {
  if (write.clock <= cellFloor(clocks, seed, write.path)) return false;
  putWinningWrite(cell, clocks, write);
  clocks.set(write.path, write.clock);
  return true;
}

// src/copy-expr.ts
function conversionFn(to, optional) {
  if (to === "json") return (v) => v;
  if (to === "counter" || to === "set")
    throw new Error(`migration: cannot convert to "${to}" column type (unsupported)`);
  const coerce = COERCE[to];
  const empty = optional ? null : to === "text" ? "" : 0;
  return (v) => {
    if (v == null) return empty;
    const out = coerce(v);
    if (out === REFUSED) throw new CastRefusedError(to, v);
    return out;
  };
}
var CastRefusedError = class _CastRefusedError extends Error {
  constructor(toType, value, reason, column) {
    const shown = typeof value === "number" ? String(value) : JSON.stringify(value);
    super(`migration: ${reason === void 0 ? `cannot convert to ${toType}: ${shown}` : reason}`);
    this.toType = toType;
    this.value = value;
    this.reason = reason;
    this.column = column;
    this.name = "CastRefusedError";
  }
  toType;
  value;
  reason;
  column;
  at(column) {
    return new _CastRefusedError(this.toType, this.value, this.reason, column);
  }
};
var sourceName = (s) => s.path ? `${s.col}.${s.path}` : s.col;
var MAX_TRANSFORM_STRING_LENGTH = 1024 * 1024;
var TransformOutputTooLargeError = class extends Error {
  constructor(length, value) {
    super(
      `migration: a transform produced a string of ${length} characters, over the limit of ${MAX_TRANSFORM_STRING_LENGTH}`
    );
    this.length = length;
    this.value = value;
    this.name = "TransformOutputTooLargeError";
  }
  length;
  value;
};
function checkTransformResult(value) {
  const pending = [value];
  const seen = /* @__PURE__ */ new Set();
  while (pending.length > 0) {
    const v = pending.pop();
    if (typeof v === "string") {
      if (v.length > MAX_TRANSFORM_STRING_LENGTH)
        throw new TransformOutputTooLargeError(v.length, v);
    } else if (typeof v === "object" && v !== null && !seen.has(v)) {
      seen.add(v);
      for (const [k, item] of Object.entries(v)) pending.push(k, item);
    }
  }
  return value;
}
var REFUSED = /* @__PURE__ */ Symbol("refused");
var INTEGER_TEXT = /^-?\d+$/;
var DECIMAL_TEXT = /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/;
var underflowed = (s, n) => n === 0 && /[1-9]/.test(s.split(/[eE]/)[0]);
var withoutNegativeZero = (n) => n === 0 ? 0 : n;
var COERCE = {
  text: (v) => typeof v === "string" ? v : JSON.stringify(v),
  boolean: (v) => {
    if (v === true || v === 1 || v === "true" || v === "1") return 1;
    if (v === false || v === 0 || v === "false" || v === "0") return 0;
    return REFUSED;
  },
  integer: (v) => {
    if (typeof v === "boolean") return v ? 1 : 0;
    const n = typeof v === "number" ? v : typeof v === "string" && INTEGER_TEXT.test(v) ? Number(v) : NaN;
    if (!Number.isSafeInteger(n)) return REFUSED;
    return withoutNegativeZero(n);
  },
  real: (v) => {
    if (typeof v === "boolean") return v ? 1 : 0;
    if (typeof v === "number") return Number.isFinite(v) ? withoutNegativeZero(v) : REFUSED;
    if (typeof v !== "string" || !DECIMAL_TEXT.test(v)) return REFUSED;
    const n = Number(v);
    return Number.isFinite(n) && !underflowed(v, n) ? withoutNegativeZero(n) : REFUSED;
  }
};
function deepSet(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (isUnsafeSegment(parts[i])) throw new Error(`unsafe path segment: ${parts[i]}`);
    if (typeof cur[parts[i]] !== "object" || cur[parts[i]] === null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  const last = parts[parts.length - 1];
  if (isUnsafeSegment(last)) throw new Error(`unsafe path segment: ${last}`);
  cur[last] = value;
}
function getPathEntry(obj, path) {
  const parts = path.split(".");
  if (parts.some(isUnsafeSegment)) return { found: false, value: void 0 };
  return ownPathEntry(obj, parts);
}
function getPath(obj, path) {
  return getPathEntry(obj, path).value;
}
function deepUnset(obj, path) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (isUnsafeSegment(parts[i])) return;
    const next = cur[parts[i]];
    if (typeof next !== "object" || next === null) return;
    cur = next;
  }
  const last = parts[parts.length - 1];
  if (isUnsafeSegment(last)) return;
  delete cur[last];
}
function parseJsonSource(raw) {
  if (raw === void 0) throw new Error("copy-expr: declared json column is absent from the row");
  if (raw === null) return null;
  return JSON.parse(raw);
}
function decodeOpJson(v) {
  if (v === void 0) throw new Error("copy-expr: declared json column is absent from the op");
  return typeof v === "string" ? JSON.parse(v) : v;
}
function evalCopy(row, step, isJsonCol, stored) {
  const decodeJson = (raw) => stored ? parseJsonSource(raw) : decodeOpJson(raw);
  const ctx = {};
  for (const s of step.sources) {
    const raw = row[s.col];
    const path = s.path ?? "";
    if (stored && raw === "" && isJsonCol(s.col)) {
      if (path !== "") {
        ctx[s.col] = void 0;
        continue;
      }
      const toType = step.cast === void 0 ? "json" : step.cast.targetType;
      throw new CastRefusedError(
        toType,
        raw,
        `cannot copy from ${step.table}/${String(row.id)} ${s.col}: the column was never written ('')`,
        s.col
      );
    }
    if (path === "") {
      ctx[s.col] = isJsonCol(s.col) ? decodeJson(raw) : raw;
    } else {
      ctx[s.col] = getPath(decodeJson(raw), path);
    }
  }
  if (!step.fn) throw new Error("copy-expr: step.fn is not resolved for this tier");
  if (step.cast === void 0) return checkTransformResult(step.fn(ctx));
  try {
    return step.fn(ctx);
  } catch (e) {
    if (e instanceof CastRefusedError) throw e.at(sourceName(step.sources[0]));
    throw e;
  }
}
function evalCopyOverOp(values, step, isJsonCol) {
  return evalCopy(values, step, isJsonCol, false);
}
function resolveCastFn(cast, srcCol) {
  const coerce = conversionFn(cast.targetType, cast.optional);
  return (ctx) => coerce(ctx[srcCol]);
}
function isNeverWrittenJsonCast(row, step, isJsonCol) {
  if (step.cast?.targetType !== "json" || (step.target.path ?? "") !== "") return false;
  const [src] = step.sources;
  return (src.path ?? "") === "" && isJsonCol(src.col) && row[src.col] === "";
}
function computeCopyTarget(row, step, isJsonCol) {
  if (isNeverWrittenJsonCast(row, step, isJsonCol)) return step.cast.optional ? null : "";
  const raw = evalCopy(row, step, isJsonCol, true);
  const result = typeof raw === "number" ? withoutNegativeZero(raw) : raw;
  const targetPath = step.target.path ?? "";
  if (targetPath === "") return isJsonCol(step.target.col) ? JSON.stringify(result) : result;
  const stored = row[step.target.col];
  if (stored === void 0)
    throw new Error("copy-expr: declared json column is absent from the row");
  const cell = readCell(stored);
  cellPut(cell, targetPath, result);
  return writeCell(cell);
}

// src/hlc.ts
var MILLIS_SHIFT = 26n;
var COUNTER_SHIFT = 18n;
var COUNTER_BITS = MILLIS_SHIFT - COUNTER_SHIFT;
var COUNTER_MASK = (1n << COUNTER_BITS) - 1n;
var NODE_MASK = (1n << COUNTER_SHIFT) - 1n;
var MAX_MILLIS = (1n << 38n) - 1n;
var DEFAULT_EPOCH_MS = 174e10;
var ZERO16 = "0000000000000000";
var HLC_HEX_RE = /^[0-9A-F]{16}$/;
function isHlcHex(v) {
  return typeof v === "string" && HLC_HEX_RE.test(v);
}
function packHlcVal(relMillis, counter, node) {
  if (relMillis < 0n || relMillis > MAX_MILLIS)
    throw new Error(`packHlcVal: relMillis out of range: ${relMillis}`);
  if (counter < 0n || counter > COUNTER_MASK)
    throw new Error(`packHlcVal: counter out of range: ${counter}`);
  if (node < 0n || node > NODE_MASK) throw new Error(`packHlcVal: node out of range: ${node}`);
  return relMillis << MILLIS_SHIFT | counter << COUNTER_SHIFT | node;
}
function hlcNext(local, observed, relMillis) {
  const mcLocal = local >> COUNTER_SHIFT;
  const mcObserved = observed >> COUNTER_SHIFT;
  const mcMax = mcLocal > mcObserved ? mcLocal : mcObserved;
  const mcNow = relMillis << COUNTER_BITS;
  const mcNext = mcNow > mcMax ? mcNow : mcMax + 1n;
  if (mcNext > (MAX_MILLIS << COUNTER_BITS | COUNTER_MASK))
    throw new Error(`hlcNext: millis overflow past 38 bits: ${mcNext >> COUNTER_BITS}`);
  return mcNext << COUNTER_SHIFT;
}
function formatHlc(packed) {
  return packed.toString(16).toUpperCase().padStart(16, "0");
}
function hexPack(relMillis, counter) {
  return formatHlc(packHlcVal(BigInt(relMillis), BigInt(counter), 0n));
}
function hexCompare(a, b) {
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
}

// src/chain-step.ts
function maxHex(hexes) {
  let out = hexes[0] ?? ZERO16;
  for (const h of hexes) if (hexCompare(h, out) > 0) out = h;
  return out;
}
function pathClocksOf(values) {
  const pc = values.__clocks;
  if (pc === void 0) return void 0;
  if (pc === null || typeof pc !== "object" || Array.isArray(pc))
    throw new Error(`chainStepOverRow: __clocks must be an object, got ${JSON.stringify(pc)}`);
  for (const [key, stamp] of Object.entries(pc))
    if (typeof stamp !== "string")
      throw new Error(`chainStepOverRow: __clocks["${key}"] must be a string stamp`);
  return pc;
}
function pathEdits(values, col) {
  const pc = pathClocksOf(values);
  if (!pc) return [];
  const prefix = `${col}.`;
  const edits = [];
  for (const [k, stamp] of Object.entries(pc))
    if (k.startsWith(prefix)) edits.push([k.slice(prefix.length), stamp]);
  if (edits.length > 0 && !(col in values))
    throw new Error(
      `chainStepOverRow: __clocks has "${col}.*" keys but the op carries no "${col}"`
    );
  return edits;
}
function setPathClocks(values, pc) {
  if (Object.keys(pc).length === 0) delete values.__clocks;
  else values.__clocks = pc;
}
function rekeyPathClocks(values, from, to) {
  if (to !== null && pathEdits(values, to).length > 0)
    throw new Error(`chainStepOverRow: rename onto "${to}", which already has __clocks keys`);
  const edits = pathEdits(values, from);
  if (edits.length === 0) return;
  const pc = pathClocksOf(values);
  for (const [path, stamp] of edits) {
    delete pc[`${from}.${path}`];
    if (to !== null) pc[`${to}.${path}`] = stamp;
  }
  setPathClocks(values, pc);
}
var isAncestorOrSelf2 = (anc, path) => path === anc || path.startsWith(`${anc}.`);
function carriedStamp(values, s, rowHlc) {
  if (!(s.col in values)) return null;
  const edits = pathEdits(values, s.col);
  if (edits.length === 0) return rowHlc;
  const read = s.path ?? "";
  if (read === "") return null;
  const covering = edits.filter(([p]) => isAncestorOrSelf2(p, read)).map(([, stamp]) => stamp);
  return covering.length === 0 ? null : maxHex(covering);
}
function isPerPathJsonMove(step, values) {
  if (step.cast?.targetType !== "json" || step.sources.length !== 1) return false;
  const [s] = step.sources;
  if ((s.path ?? "") !== "" || (step.target.path ?? "") !== "") return false;
  return pathEdits(values, s.col).length > 0;
}
function copySourcesCarried(step, values) {
  if (isPerPathJsonMove(step, values)) return true;
  return step.sources.every((s) => carriedStamp(values, s, ZERO16) !== null);
}
function asObject(v, what) {
  if (v === null || typeof v !== "object" || Array.isArray(v))
    throw new Error(`chainStepOverRow: ${what} must be a json object, got ${JSON.stringify(v)}`);
  return v;
}
function movePerPathJson(step, values) {
  const src = step.sources[0].col;
  const tgt = step.target.col;
  if (pathEdits(values, tgt).length > 0 || tgt in values)
    throw new Error(`chainStepOverRow: per-path copy onto "${tgt}", which the op already writes`);
  values[tgt] = structuredClone(asObject(values[src], `per-path edit of "${src}"`));
  const pc = pathClocksOf(values);
  for (const [path, stamp] of pathEdits(values, src)) pc[`${tgt}.${path}`] = stamp;
}
function placeCopyResult(values, target, result, stamp, isJson) {
  const col = target.col;
  const path = target.path ?? "";
  if (path === "") {
    const stale = pathEdits(values, col);
    values[col] = isJson(col) ? JSON.stringify(result) : result;
    if (stale.length === 0) return;
    const pc = pathClocksOf(values);
    for (const [p] of stale) delete pc[`${col}.${p}`];
    setPathClocks(values, pc);
    return;
  }
  const edits = pathEdits(values, col);
  if (col in values && edits.length === 0) {
    const held = values[col];
    const cell = held === null ? {} : { v: decodeOpJson(held) };
    cellPut(cell, path, result);
    values[col] = writeCell(cell);
    return;
  }
  for (const [p] of edits)
    if (isAncestorOrSelf2(p, path) || isAncestorOrSelf2(path, p))
      throw new Error(
        `chainStepOverRow: copy into "${col}.${path}" overlaps the op's per-path edit "${col}.${p}"`
      );
  const partial = col in values ? asObject(values[col], `per-path edit of "${col}"`) : {};
  deepSet(partial, path, result);
  values[col] = partial;
  setPathClocks(values, { ...pathClocksOf(values), [`${col}.${path}`]: stamp });
}
function chainStepOverRow(step, values, clocks, rowHlc, isJson) {
  switch (step.type) {
    case "addColumn":
      return;
    case "renameColumn":
      rekeyPathClocks(values, step.from, step.to);
      if (step.from in values) {
        values[step.to] = values[step.from];
        delete values[step.from];
      }
      if (step.from in clocks) {
        clocks[step.to] = clocks[step.from];
        delete clocks[step.from];
      }
      return;
    case "dropColumn":
      rekeyPathClocks(values, step.column, null);
      delete values[step.column];
      delete clocks[step.column];
      return;
    case "copyFromExpression": {
      if (isPerPathJsonMove(step, values)) {
        movePerPathJson(step, values);
      } else {
        const stamps = step.sources.map((s) => carriedStamp(values, s, rowHlc));
        if (stamps.some((s) => s === null)) return;
        const result = evalCopyOverOp(values, step, isJson);
        const stamp = maxHex(stamps.length === 0 ? [rowHlc] : stamps);
        placeCopyResult(values, step.target, result, stamp, isJson);
      }
      clocks[step.target.col] = maxHex(
        step.sources.length === 0 ? [rowHlc] : step.sources.map((s) => clocks[s.col] ?? rowHlc)
      );
      return;
    }
    // These change tables, indexes or column metadata, never a staged row's values.
    case "addTable":
    case "destroyTable":
    case "renameTable":
    case "addIndex":
    case "dropIndex":
    case "setColumnMeta":
      return;
    default:
      throw new Error(
        `chainStepOverRow: unrecognized migration step type ${step.type}`
      );
  }
}

// src/json-schema.ts
function isNode(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function typeMatches(t, value) {
  switch (t) {
    case "string":
      return typeof value === "string";
    case "number":
      return typeof value === "number";
    case "integer":
      return typeof value === "number" && Number.isInteger(value);
    case "boolean":
      return typeof value === "boolean";
    case "null":
      return value === null;
    case "object":
      return typeof value === "object" && value !== null && !Array.isArray(value);
    case "array":
      return Array.isArray(value);
    default:
      return true;
  }
}
function branchesOf(schema) {
  const raw = schema.anyOf ?? schema.oneOf;
  return Array.isArray(raw) ? raw.filter(isNode) : void 0;
}
function discriminatorOf(schema) {
  const d = schema.discriminator;
  if (d === void 0) return void 0;
  if (!isNode(d) || typeof d.propertyName !== "string")
    throw new Error(`json-schema: malformed discriminator ${JSON.stringify(d)}`);
  return d.propertyName;
}
function validateTagged(branches, tag, value, opts) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  if (!Object.hasOwn(value, tag))
    return opts?.tagOptional === true && branches.some((b) => validateJsonValue(b, value, opts));
  const tagValue = value[tag];
  const selected = branches.filter((b) => claimsTag(b, tag, tagValue));
  return selected.length === 1 && validateJsonValue(selected[0], value, opts);
}
function claimsTag(branch, tag, tagValue) {
  const props = isNode(branch.properties) ? branch.properties : void 0;
  const sub = props && Object.hasOwn(props, tag) ? props[tag] : void 0;
  if (isNode(sub)) return validateJsonValue(sub, tagValue);
  if (Array.isArray(branch.oneOf) && discriminatorOf(branch) !== void 0)
    return branch.oneOf.filter(isNode).some((b) => claimsTag(b, tag, tagValue));
  throw new Error(
    `json-schema: a branch of the union tagged "${tag}" declares no "${tag}" property`
  );
}
function assertNoRef(schema) {
  if (schema.$ref !== void 0)
    throw new Error(`json-schema: $ref ${JSON.stringify(schema.$ref)} is not supported`);
}
function validateJsonValue(schema, value, opts) {
  assertNoRef(schema);
  const anyOf = Array.isArray(schema.anyOf) ? schema.anyOf.filter(isNode) : void 0;
  if (anyOf) return anyOf.some((b) => validateJsonValue(b, value, opts));
  const oneOf = Array.isArray(schema.oneOf) ? schema.oneOf.filter(isNode) : void 0;
  if (oneOf) {
    const tag = discriminatorOf(schema);
    if (tag !== void 0) return validateTagged(oneOf, tag, value, opts);
    return oneOf.filter((b) => validateJsonValue(b, value, opts)).length === 1;
  }
  const t = schema.type;
  if (typeof t === "string" && !typeMatches(t, value)) return false;
  if (Array.isArray(t) && !t.some((x) => typeof x === "string" && typeMatches(x, value))) {
    return false;
  }
  if (Array.isArray(schema.enum) && !schema.enum.includes(value)) return false;
  if ("const" in schema && schema.const !== value) return false;
  if (Array.isArray(value)) {
    const items = schema.items;
    if (isNode(items)) return value.every((el) => validateJsonValue(items, el, opts));
    return true;
  }
  if (typeof value === "object" && value !== null) {
    const obj = value;
    const props = isNode(schema.properties) ? schema.properties : void 0;
    const addl = schema.additionalProperties;
    for (const [k, v] of Object.entries(obj)) {
      const sub = props && Object.hasOwn(props, k) ? props[k] : void 0;
      if (isNode(sub)) {
        if (!validateJsonValue(sub, v, opts)) return false;
      } else if (addl === false) {
        return false;
      } else if (isNode(addl) && !validateJsonValue(addl, v, opts)) {
        return false;
      }
    }
    if (opts?.partial !== true && Array.isArray(schema.required)) {
      for (const r of schema.required) {
        if (typeof r === "string" && !Object.hasOwn(obj, r)) return false;
      }
    }
    return true;
  }
  return true;
}
function isWildcard(schema) {
  return schema.type === void 0 && schema.properties === void 0 && schema.additionalProperties === void 0 && schema.items === void 0 && schema.anyOf === void 0 && schema.oneOf === void 0 && schema.enum === void 0 && !("const" in schema);
}
function pathWalk(schema, parts, i) {
  if (i === parts.length) return true;
  const seg = parts[i];
  if (seg === "" || isUnsafeSegment(seg)) return false;
  assertNoRef(schema);
  const branches = branchesOf(schema);
  if (branches) return branches.some((b) => pathWalk(b, parts, i));
  if (isWildcard(schema)) return true;
  const t = schema.type;
  const props = isNode(schema.properties) ? schema.properties : void 0;
  const addl = schema.additionalProperties;
  const isObject = t === "object" || Array.isArray(t) && t.includes("object") || t === void 0 && (props !== void 0 || isNode(addl));
  if (isObject) {
    const sub = props && Object.hasOwn(props, seg) ? props[seg] : void 0;
    if (isNode(sub)) return pathWalk(sub, parts, i + 1);
    if (isNode(addl)) return pathWalk(addl, parts, i + 1);
    return addl === true;
  }
  const isArray = t === "array" || Array.isArray(t) && t.includes("array");
  if (isArray && /^\d+$/.test(seg)) {
    const items = schema.items;
    if (isNode(items)) return pathWalk(items, parts, i + 1);
    return true;
  }
  return false;
}
function schemaPathExists(schema, path) {
  return pathWalk(schema, path.split("."), 0);
}
function writtenPathsConform(schema, value, paths) {
  return pathsConform(
    schema,
    value,
    // The root path "" is zero segments: the whole value is judged.
    paths.map((p) => p === "" ? [] : p.split("."))
  );
}
function pathsConform(schema, value, paths) {
  if (value === void 0) return true;
  if (paths.some((p) => p.length === 0)) return validateJsonValue(schema, value, { partial: true });
  assertNoRef(schema);
  const branches = branchesOf(schema);
  if (branches) {
    const tag = discriminatorOf(schema);
    if (tag === void 0) {
      const fits = branches.filter((b) => pathsConform(b, value, paths)).length;
      return Array.isArray(schema.anyOf) ? fits > 0 : fits === 1;
    }
    if (typeof value !== "object" || value === null || !Object.hasOwn(value, tag)) return false;
    const tagValue = value[tag];
    const selected = branches.filter((b) => claimsTag(b, tag, tagValue));
    if (selected.length !== 1) return false;
    if (paths.some((p) => p[0] === tag))
      return validateJsonValue(selected[0], value, { partial: true });
    return pathsConform(selected[0], value, paths);
  }
  if (isWildcard(schema)) return true;
  if (Array.isArray(value)) {
    const items = schema.items;
    if (!typeMatchesSchema(schema, value)) return false;
    if (!isNode(items)) return true;
    return groupByHead(paths).every(([seg, rest]) => pathsConform(items, value[Number(seg)], rest));
  }
  if (typeof value !== "object" || value === null || !typeMatchesSchema(schema, value))
    return validateJsonValue(schema, value, { partial: true });
  const obj = value;
  const props = isNode(schema.properties) ? schema.properties : void 0;
  const addl = schema.additionalProperties;
  return groupByHead(paths).every(([seg, rest]) => {
    const sub = props && Object.hasOwn(props, seg) ? props[seg] : void 0;
    const child = Object.hasOwn(obj, seg) ? obj[seg] : void 0;
    if (isNode(sub)) return pathsConform(sub, child, rest);
    if (isNode(addl)) return pathsConform(addl, child, rest);
    return addl !== false || child === void 0;
  });
}
function typeMatchesSchema(schema, value) {
  const t = schema.type;
  if (typeof t === "string") return typeMatches(t, value);
  if (Array.isArray(t)) return t.some((x) => typeof x === "string" && typeMatches(x, value));
  return true;
}
function groupByHead(paths) {
  const out = /* @__PURE__ */ new Map();
  for (const [head, ...rest] of paths) {
    const g = out.get(head);
    if (g) g.push(rest);
    else out.set(head, [rest]);
  }
  return [...out];
}

// src/mutations.ts
var inc = (n) => ({ __rbInc: n });
var add = (...e) => ({ __rbAdd: e });
var remove = (...e) => ({ __rbRemove: e });
var isObj = (v) => typeof v === "object" && v !== null;
var isInc = (v) => isObj(v) && "__rbInc" in v;
var isAdd = (v) => isObj(v) && "__rbAdd" in v;
var isRemove = (v) => isObj(v) && "__rbRemove" in v;
var SENTINEL_KEYS = ["__rbInc", "__rbAdd", "__rbRemove"];
function assertSingleSentinel(v) {
  if (!isObj(v)) return;
  const present = SENTINEL_KEYS.filter((k) => k in v);
  if (present.length > 1)
    throw new Error(
      `mutation value carries multiple sentinels (${present.join(", ")}) \u2014 a value must be exactly one of rb.inc/rb.add/rb.remove`
    );
}

// src/set-sort.ts
function setElementCompare(a, b) {
  const sa = String(a);
  const sb = String(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}
function sortSetElements(elems) {
  return [...elems].sort(setElementCompare);
}

// src/unwritten.ts
var UNWRITTEN_FIELD = "__unwritten";
export {
  COUNTER_BITS,
  COUNTER_MASK,
  COUNTER_SHIFT,
  CastRefusedError,
  DEFAULT_EPOCH_MS,
  MAX_TRANSFORM_STRING_LENGTH,
  MILLIS_SHIFT,
  NODE_MASK,
  TransformOutputTooLargeError,
  UNWRITTEN_FIELD,
  ZERO16,
  add,
  applyCellWrite,
  assertSingleSentinel,
  cellDelete,
  cellFloor,
  cellPut,
  cellRead,
  chainStepOverRow,
  computeCopyTarget,
  conversionFn,
  copySourcesCarried,
  decodeOpJson,
  deepSet,
  deepUnset,
  formatHlc,
  getPath,
  getPathEntry,
  hexCompare,
  hexPack,
  hlcNext,
  inc,
  isAdd,
  isAncestorOrSelf,
  isHlcHex,
  isInc,
  isRemove,
  isUnsafeSegment,
  packHlcVal,
  putWinningWrite,
  readCell,
  remove,
  resolveCastFn,
  schemaPathExists,
  setElementCompare,
  setTreePath,
  sortSetElements,
  validateJsonValue,
  writeCell,
  writtenPathsConform
};
